import { useCallback, useState } from 'react';
import { MAX_REFERENCE_PROMPTS, MENTION_COLOR_MAP } from '@/app/constants';
import { useStoreApi } from '@/app/providers/store';
import type { ReferencePrompt, TextPromptWithInputItem } from '@/app/types';
import { parseTextToTextPromptWithReference, stringifyTextPromptWithReference } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { getToolTypeConfig } from '../components/HeroSection/ImmersiveHero/utils';
import type { HeroPresetPrompt, HeroToolType, LocalImage } from '../types';
import { encodeHttpMediaUrl } from '../utils/media-url';

export function useHeroToolForm(toolType: HeroToolType | null, buttonHref?: string) {
  const router = useRouter();
  const storeApi = useStoreApi();

  const [textPrompt, setTextPrompt] = useState<TextPromptWithInputItem>([]);
  const [referenceImages, setReferenceImages] = useState<LocalImage[]>([]);

  const config = toolType ? getToolTypeConfig(toolType) : null;
  const needsImages = config?.needsImages ?? false;
  const needsMentions = config?.needsMentions ?? false;

  const handlePresetClick = useCallback(
    (preset: HeroPresetPrompt) => {
      const presetText = preset.promptText ? [{ type: 'text' as const, text: preset.promptText }] : [];

      if (needsImages && preset.referenceImages?.length) {
        const images: LocalImage[] = preset.referenceImages.map((url, i) => ({
          id: `preset-${i}`,
          url: encodeHttpMediaUrl(url),
          name: `image${i + 1}`,
        }));

        setReferenceImages(images);

        if (needsMentions) {
          const refs: ReferencePrompt[] = images.map((img) => ({
            type: 'image' as const,
            id: img.id,
            name: img.name || '',
            status: 'done' as const,
            prompt: {
              sourceImage: img.url,
              sourceImagePreview: img.url,
              selectedImage: img.url,
              selectedImagePreview: img.url,
            },
          }));
          setTextPrompt(parseTextToTextPromptWithReference(preset.promptText, refs));
        } else {
          setTextPrompt(presetText);
        }
      } else {
        setReferenceImages([]);
        setTextPrompt(presetText);
      }
    },
    [needsImages, needsMentions],
  );

  const handleCreate = useCallback(() => {
    if (!toolType || !buttonHref || !config) return;

    const { updateFormParams, updateModelVersion } = storeApi.getState();

    const { formType, modelVersion } = config;
    const plainText = stringifyTextPromptWithReference(textPrompt);

    updateModelVersion(formType, modelVersion);
    updateFormParams(formType, 'textPrompt', plainText);

    if (config.needsMentions) {
      updateFormParams(formType, 'textPromptWithReference', textPrompt);
    }

    if (needsImages && referenceImages.length > 0) {
      const referencePrompts = referenceImages.map((img) => ({
        type: 'image' as const,
        id: crypto.randomUUID(),
        name: img.name || 'image',
        status: 'done' as const,
        prompt: {
          sourceImage: img.url,
          sourceImagePreview: img.url,
          selectedImage: img.url,
          selectedImagePreview: img.url,
          ...(img.file ? { _tempFile: img.file } : {}),
        },
      }));

      updateFormParams(formType, 'imagePrompts', referencePrompts);
      updateFormParams(formType, 'referencePrompts', referencePrompts);
    }

    router.push(buttonHref);
  }, [toolType, buttonHref, config, storeApi, textPrompt, needsImages, referenceImages, router]);

  const handleImageUpload = useCallback(
    (file: File) => {
      if (referenceImages.length >= MAX_REFERENCE_PROMPTS) return;

      const url = URL.createObjectURL(file);
      const id = crypto.randomUUID();

      setReferenceImages((prev) => [...prev, { id, url, name: file.name, file }]);

      if (needsMentions) {
        const mentionName = `image${referenceImages.length + 1}`;
        setTextPrompt((prev) => [
          ...prev,
          ...(prev.length > 0 ? [{ type: 'text' as const, text: ' ' }] : []),
          {
            type: 'mention' as const,
            mention: {
              id,
              name: mentionName,
              color: MENTION_COLOR_MAP.image,
            },
          },
        ]);
      }
    },
    [needsMentions, referenceImages.length],
  );

  const handleRemoveImage = useCallback(
    (index: number) => {
      setReferenceImages((prev) => {
        const removed = prev[index];
        const next = [...prev];
        if (removed?.url.startsWith('blob:')) {
          URL.revokeObjectURL(removed.url);
        }
        next.splice(index, 1);

        if (removed && needsMentions) {
          setTextPrompt((prevPrompt) =>
            prevPrompt.filter((item) => !(item.type === 'mention' && item.mention.id === removed.id)),
          );
        }

        return next;
      });
    },
    [needsMentions],
  );

  return {
    textPrompt,
    setTextPrompt,
    referenceImages,
    handlePresetClick,
    handleCreate,
    handleImageUpload,
    handleRemoveImage,
  };
}
